/**
 * First-run Environment step. After CMMD identity, before Workspace import.
 *
 * Success with an unpublished Prepared contract is an Environment id.
 * ensure uses a CMMD user JWT on owner forge-context, never a runtime grant.
 *
 * The POST body here is only `{ organizationId }`, but CMMD's ensureRuntime
 * calls ensureRuntimeMachine unconditionally: a successful ensure always
 * creates or resumes a cloud sprite in that organization. This is exactly
 * why `ensureSeatEnvironment` must never fire without an explicit user
 * action naming the organization first. See `useFirstRunEnvironment`, which
 * resolves the organization and shows it, then waits for a click before
 * calling this.
 */

export type FirstRunEnvironmentEntry =
  | { readonly kind: "dead-end"; readonly reason: "missing-seat" }
  | { readonly kind: "step"; readonly stepId: "environment" }
  | { readonly kind: "continue" };

export function firstRunEnvironmentEntry(input: {
  readonly hasSeat: boolean;
  readonly hasEnvironmentId: boolean;
  readonly setupCompleted: boolean;
}): FirstRunEnvironmentEntry {
  if (!input.hasSeat) {
    return { kind: "dead-end", reason: "missing-seat" };
  }
  if (!input.hasEnvironmentId) {
    return { kind: "step", stepId: "environment" };
  }
  if (input.setupCompleted) return { kind: "continue" };
  return { kind: "step", stepId: "environment" };
}

export function classifyEnsureFailure(input: {
  readonly status: number;
  readonly code: string | null;
  readonly error: string | null;
}): "missing-seat" | "failed" {
  const haystack = `${input.code ?? ""} ${input.error ?? ""}`.toLowerCase();
  if (haystack.includes("seat") || input.status === 402) return "missing-seat";
  return "failed";
}

export const FIRST_RUN_ENSURE_PATH = "/api/cmmd/forge-runtimes/ensure" as const;

export function firstRunEnsureUsesOwnerJwt(): true {
  return true;
}

export const MISSING_SEAT_COPY =
  "This CMMD organization membership has no Seat, so Forge cannot create an Environment.";

export async function ensureSeatEnvironment(input: {
  readonly organizationId: number | null;
  readonly fetchImpl?: typeof fetch;
  readonly requestUrl: (path: string) => string;
  readonly requestInit: (init: RequestInit) => RequestInit;
}): Promise<
  | { readonly kind: "ok"; readonly environmentId: string; readonly runtimeId: string }
  | { readonly kind: "missing-seat" }
  | { readonly kind: "failed"; readonly message: string }
> {
  if (input.organizationId === null) {
    return { kind: "failed", message: "CMMD did not return an organization." };
  }
  const fetchImpl = input.fetchImpl ?? fetch;
  const response = await fetchImpl(
    input.requestUrl(FIRST_RUN_ENSURE_PATH),
    input.requestInit({
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ organizationId: input.organizationId }),
    }),
  );
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      readonly code?: string;
      readonly error?: string;
    };
    if (
      classifyEnsureFailure({
        status: response.status,
        code: body.code ?? null,
        error: body.error ?? null,
      }) === "missing-seat"
    ) {
      return { kind: "missing-seat" };
    }
    return { kind: "failed", message: body.error ?? `CMMD returned ${response.status}.` };
  }
  const body = (await response.json()) as {
    readonly runtime?: {
      readonly environmentId?: string | null;
      readonly runtimeId?: string | null;
    };
  };
  const environmentId = body.runtime?.environmentId ?? body.runtime?.runtimeId ?? null;
  if (environmentId === null || environmentId.length === 0) {
    return { kind: "failed", message: "CMMD did not return an Environment id." };
  }
  const runtimeId = body.runtime?.runtimeId ?? environmentId;
  return { kind: "ok", environmentId, runtimeId };
}
